import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useAuth } from './auth/AuthProvider';
import SignIn from './auth/SignIn';
import Shell from './components/Shell';
import ApprovalPage from './pages/ApprovalPage';
import CandidateForm from './pages/CandidateForm.jsx';
import ApplicationForm from './pages/ApplicationForm.jsx';
import IntakeForm from './pages/IntakeForm';
import MatterDetail from './pages/MatterDetail';
import MatterList from './pages/MatterList';
import ApprovalQueue from './pages/ApprovalQueue';
import Templates from './pages/Templates';
import AdminLayout from './pages/admin/AdminLayout';
import AdminRoster from './pages/admin/AdminRoster';
import AdminApprovalFlow from './pages/admin/AdminApprovalFlow';
import AdminFieldBuilder from './pages/admin/AdminFieldBuilder';
import AdminAreasOfLaw from './pages/admin/AdminAreasOfLaw';
import AdminNumbering from './pages/admin/AdminNumbering';
import AdminConflicts from './pages/admin/AdminConflicts';
import AdminDirectories from './pages/admin/AdminDirectories';
import AdminPdfLayout from './pages/admin/AdminPdfLayout';
import CandidateForms from './pages/CandidateForms.jsx';
import Applications from './pages/Applications.jsx';
import ApplicationFormEditor from './pages/ApplicationFormEditor.jsx';
import Help from './pages/Help.jsx';
import AdminKeywordAlerts from './pages/admin/AdminKeywordAlerts';
import AdminOrbitFeed from './pages/admin/AdminOrbitFeed';
import AdminHelpPage from './pages/admin/AdminHelpPage';
import AdminMailQueue from './pages/admin/AdminMailQueue';
import IntakeNumbering from './pages/IntakeNumbering';
import WorkflowTracking from './pages/WorkflowTracking';
import ConflictsStandalone from './pages/ConflictsStandalone';

function RequireAuth({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <Loading />;
  if (!user) return <SignIn />;
  return children;
}

/** Keeps the page's earlier paths working, query string and all. */
function CandidateFormsRedirect() {
  const { search } = useLocation();
  return <Navigate to={`/ccc${search}`} replace />;
}

function RequireAdmin({ children }) {
  const { isAdmin, loading } = useAuth();
  if (loading) return <Loading />;
  if (!isAdmin) {
    return (
      <p className="rounded bg-white dark:bg-slate-900 p-6 text-sm text-slate-600 dark:text-slate-300 shadow">
        You need the <strong>admin</strong> role to view this page.
      </p>
    );
  }
  return children;
}

/**
 * Guards the Intake numbering page. Mirrors RequireAdmin exactly except it checks
 * `isIntake` instead of `isAdmin` — client number, matter number, and matter name are
 * exclusively an Intake action now, so this is deliberately NOT satisfied by
 * isAdmin/isSuperAdmin (see AuthProvider.jsx and functions/index.js's requireIntake).
 */
function RequireIntake({ children }) {
  const { isIntake, loading } = useAuth();
  if (loading) return <Loading />;
  if (!isIntake) {
    return (
      <p className="rounded bg-white dark:bg-slate-900 p-6 text-sm text-slate-600 dark:text-slate-300 shadow">
        You need the <strong>Intake</strong> role to view this page.
      </p>
    );
  }
  return children;
}

/**
 * Guards the workflow tracking/reporting page. Allows admins, superadmins (superadmin
 * is implied by isAdmin — see AuthProvider.jsx), the read-only Workflow Viewer role,
 * and the dedicated Reminder Sender role.
 */
function RequireWorkflowAccess({ children }) {
  const { isAdmin, isWorkflowViewer, isReminderSender, loading } = useAuth();
  if (loading) return <Loading />;
  if (!isAdmin && !isWorkflowViewer && !isReminderSender) {
    return (
      <p className="rounded bg-white dark:bg-slate-900 p-6 text-sm text-slate-600 dark:text-slate-300 shadow">
        You need the <strong>admin</strong>, <strong>Workflow Viewer</strong>, or{' '}
        <strong>Reminder Sender</strong> role to view this page.
      </p>
    );
  }
  return children;
}

function Loading() {
  return <div className="p-10 text-center text-sm text-slate-500 dark:text-slate-400">Loading…</div>;
}

export default function App() {
  return (
    <Routes>
      {/* Reached from a magic link in an approval email. Deliberately outside RequireAuth:
          the signed token is the credential, so an approver can act from a phone without
          having to sign in first. See functions/src/auth/middleware.js. */}
      <Route path="/approve" element={<ApprovalPage />} />

      {/* A job candidate's conflict-disclosure form, opened from a signed link in their
          invitation email. Outside RequireAuth for the same reason as /approve, and a stronger
          one: the person filling it in is not in the firm and has no account to sign in with.
          The token is the credential and every check is server-side — see
          functions/src/candidate/endpoints.js. */}
      <Route path="/candidate-form" element={<CandidateForm />} />

      {/* Candidate conflict checks, at /ccc. Signed-in and outside Shell for the same reason
          /conflicts is: this is a separate tool that happens to share a deployment, not a step
          in opening a matter, and showing it the matter navigation would imply otherwise. It is
          reached directly rather than from the intake app's rail. */}
      <Route
        path="/ccc"
        element={
          <RequireAuth>
            <CandidateForms />
          </RequireAuth>
        }
      />
      {/* An application for employment, opened from a signed link. Outside RequireAuth for the
          same reason as the candidate form above: the applicant has no firm account, and the
          token in the link is the credential. */}
      <Route path="/application" element={<ApplicationForm />} />

      {/* Where HR issues application links and reads completed ones. Signed-in, and outside
          Shell like /ccc: a separate tool sharing a deployment, not a step in opening a matter. */}
      <Route
        path="/apply"
        element={
          <RequireAuth>
            <Applications />
          </RequireAuth>
        }
      />
      {/* The application as applicants see it, with an Edit switch. Form Editors and admins. */}
      <Route
        path="/apply/form"
        element={
          <RequireAuth>
            <ApplicationFormEditor />
          </RequireAuth>
        }
      />

      {/* Both earlier homes of this page. The review link in HR's notification pointed at one of
          them, so they redirect with the query string rather than 404. */}
      <Route path="/candidate-forms" element={<CandidateFormsRedirect />} />

      {/* A signed-in, deliberately chrome-free conflicts-only intake. It remains outside
          Shell so no matter/workflow navigation is visible, unlike every main app page. */}
      <Route
        path="/conflicts"
        element={
          <RequireAuth>
            <ConflictsStandalone />
          </RequireAuth>
        }
      />

      <Route
        element={
          <RequireAuth>
            <Shell />
          </RequireAuth>
        }
      >
        {/*
          Opening the app opens the form. This is an intake tool: the thing somebody arrives to do
          is open a matter, and the list of ones they already opened is a click away on the rail.
          It was the other way round from the first commit until 1.98.0, when automatic sign-in
          started delivering people to the root and the list was the first thing they saw.
        */}
        <Route index element={<Navigate to="/matters/new" replace />} />
        <Route path="matters" element={<MatterList />} />
        <Route path="matters/new" element={<IntakeForm />} />
          {/*
            The guided practice run: the same component, the same real form, with every write
            refused. A separate route rather than a query flag so it cannot be reached by
            accident, and so the URL says plainly what you are looking at.
          */}
          <Route path="matters/practice" element={<IntakeForm practice />} />
        <Route path="matters/:matterId/edit" element={<IntakeForm />} />
        <Route path="matters/:matterId" element={<MatterDetail />} />
        <Route path="queue" element={<ApprovalQueue />} />
        <Route path="templates" element={<Templates />} />
        <Route path="help" element={<Help />} />
        <Route
          path="numbering"
          element={
            <RequireIntake>
              <IntakeNumbering />
            </RequireIntake>
          }
        />
        <Route
          path="workflow"
          element={
            <RequireWorkflowAccess>
              <WorkflowTracking />
            </RequireWorkflowAccess>
          }
        />
        <Route
          path="admin"
          element={
            <RequireAdmin>
              <AdminLayout />
            </RequireAdmin>
          }
        >
          <Route index element={<Navigate to="roster" replace />} />
          <Route path="roster" element={<AdminRoster />} />
          <Route path="approval-flow" element={<AdminApprovalFlow />} />
          {/*
            Field rules folded into Field Builder. Redirected rather than removed so a
            bookmark or a link in an old email still lands somewhere useful.
          */}
          <Route path="field-rules" element={<Navigate to="/admin/field-builder" replace />} />
          <Route path="field-builder" element={<AdminFieldBuilder />} />
          <Route path="areas-of-law" element={<AdminAreasOfLaw />} />
          <Route path="numbering" element={<AdminNumbering />} />
          <Route path="conflicts" element={<AdminConflicts />} />
          <Route path="pdf-layout" element={<AdminPdfLayout />} />
          <Route path="directories" element={<AdminDirectories />} />
          {/* Was here first, and the review link in HR's email points at it. Redirects rather
              than 404s, and keeps the ?form= query so the link still opens that form. */}
          <Route path="candidate-forms" element={<CandidateFormsRedirect />} />
          <Route path="alerts" element={<AdminKeywordAlerts />} />
          <Route path="help-page" element={<AdminHelpPage />} />
          <Route path="mail-queue" element={<AdminMailQueue />} />
          <Route path="orbit" element={<AdminOrbitFeed />} />
          {/* Folded into Directories / Conflicts / Workflow respectively — kept as redirects
              so an old bookmark or link still lands somewhere useful. */}
          <Route path="feeds" element={<Navigate to="/admin/directories" replace />} />
          <Route path="conflict-routing" element={<Navigate to="/admin/conflicts" replace />} />
          <Route path="conflicts-page" element={<Navigate to="/admin/conflicts" replace />} />
          <Route path="test-mode" element={<Navigate to="/admin/approval-flow" replace />} />
        </Route>
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
